import { ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { MaskedLine, Overline, FadeUp, Stagger, StaggerItem } from "@/components/site/Reveal";
import { ComingSoonCard } from "@/components/site/ComingSoon";
import { VideoCard } from "@/components/site/VideoCard";
import { getLatestAll } from "@/lib/api";
import { YOUTUBE_URL } from "@/data/content";

export default function Videos() {
  const { data } = useQuery({
    queryKey: ["yt-latest"],
    queryFn: getLatestAll,
    staleTime: 300000,
  });
  const videos = data?.videos || [];
  const shorts = data?.shorts || [];
  const hasVideos = videos.length > 0;
  const hasShorts = shorts.length > 0;
  const hasContent = hasVideos || hasShorts;
  const [featured, ...rest] = videos;

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pb-32 md:pt-48" data-testid="videos-page">
      <MaskedLine>
        <Overline>The Archive</Overline>
      </MaskedLine>
      <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="videos-heading">
        <MaskedLine delay={0.15}>Every</MaskedLine>
        <MaskedLine delay={0.3}><span className="text-brand-accent">Video.</span></MaskedLine>
      </h1>
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted md:text-lg">
          {hasContent
            ? "Guest rounds, Fore Shorts, challenges, and road trips — everything we make, in one place."
            : "Guest rounds, Fore Shorts, challenges, and road trips — the full library is on its way. Subscribe so you don't miss the first drop."}
        </p>
      </FadeUp>

      {hasVideos ? (
        <>
          <div data-testid="video-featured">
            <FadeUp delay={0.6} className="mt-16">
              <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-brand-accent">Latest Upload</p>
              <VideoCard
                video={{ ...featured, thumbnail: `https://i.ytimg.com/vi/${featured.id}/maxresdefault.jpg` }}
                large
                testid="video-featured-card"
              />
            </FadeUp>
          </div>
          {rest.length > 0 && (
            <div data-testid="videos-grid">
              <Stagger className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((v) => (
                  <StaggerItem key={v.id}>
                    <VideoCard video={v} testid={`video-card-${v.id}`} />
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          )}
        </>
      ) : !hasContent ? (
        <FadeUp delay={0.65} className="mt-16">
          <ComingSoonCard label="Video Coming Soon" aspect="aspect-video" className="w-full" />
        </FadeUp>
      ) : null}

      {hasShorts && (
        <div className={hasVideos ? "mt-20" : "mt-16"} data-testid="shorts-section">
          <FadeUp>
            <Overline>Fore Shorts</Overline>
          </FadeUp>
          <div data-testid="shorts-grid">
            <Stagger className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {shorts.map((s) => (
                <StaggerItem key={s.id}>
                  <VideoCard video={{ ...s, category: s.views || "Fore Short" }} vertical testid={`short-card-${s.id}`} />
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      )}

      <FadeUp delay={0.2} className="mt-10 flex flex-wrap items-center gap-6">
        <a
          href={YOUTUBE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="videos-subscribe-cta"
          className="group inline-flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
        >
          Subscribe on YouTube
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
          Guest Rounds · Fore Shorts · Challenges · On The Road
        </p>
      </FadeUp>
    </main>
  );
}
